import { motion, AnimatePresence } from 'framer-motion'
import { X, Download, Share2 } from 'lucide-react'
import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { Media } from '@capacitor-community/media'
import { useTranslation } from 'react-i18next'

interface FullscreenImageModalProps {
  isOpen: boolean
  onClose: () => void
  imageUrl: string
  imageAlt?: string
  promptText?: string
  playerName?: string
  roundNumber?: number
}

export default function FullscreenImageModal({
  isOpen,
  onClose,
  imageUrl,
  imageAlt = 'Fullscreen image',
  promptText,
  playerName,
  roundNumber
}: FullscreenImageModalProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const { t } = useTranslation()

  // Android'de Media.savePhoto için albumIdentifier zorunlu.
  // Albüm varsa bul, yoksa oluşturup kimliğini döndür.
  const getAndroidAlbumIdentifier = async (albumName: string): Promise<string | null> => {
    try {
      const albums = await Media.getAlbums()
      const found = albums.albums.find(a => a.name === albumName || a.identifier.endsWith(`/${albumName}`))
      if (found) return found.identifier
      try {
        await Media.createAlbum({ name: albumName })
      } catch (err) {
        // Albüm zaten varsa hata dönebilir; devam et
        console.warn('createAlbum warning:', err)
      }
      const albumsAfter = await Media.getAlbums()
      const created = albumsAfter.albums.find(a => a.name === albumName || a.identifier.endsWith(`/${albumName}`))
      return created ? created.identifier : null
    } catch (e) {
      console.error('getAndroidAlbumIdentifier error:', e)
      return null
    }
  }

  const handleDownload = async () => {
    try {
      const fileName = `image-${playerName || 'unknown'}-round-${roundNumber || 1}.jpg`

      // Native (iOS/Android): save to gallery using Media plugin
      if (Capacitor.isNativePlatform()) {
        const platform = Capacitor.getPlatform()
        
        try {
          // Görseli fetch et ve base64'e çevir
          const response = await fetch(imageUrl)
          const blob = await response.blob()
          const arrayBuffer = await blob.arrayBuffer()
          const bytes = new Uint8Array(arrayBuffer)
          let binary = ''
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
          const base64Data = typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64')

          // Android için: önce dosyayı kaydet, ardından hedef albüm kimliği ile galeriye taşı
          if (platform === 'android') {
            // Önce geçici dosya olarak kaydet
            const writeResult = await Filesystem.writeFile({
              path: fileName,
              data: base64Data,
              directory: Directory.Cache,
            })

            // Hedef albümü bul/oluştur
            const albumIdentifier = await getAndroidAlbumIdentifier('Scraible')
            if (!albumIdentifier) throw new Error('Album identifier not available')

            // Sonra Media plugin ile galeriye kaydet
            await Media.savePhoto({
              path: writeResult.uri,
              albumIdentifier,
            })
          } else {
            // iOS için direkt base64 yöntemi
            await Media.savePhoto({
              path: `data:image/jpeg;base64,${base64Data}`,
            })
          }
          
          alert(t('imageModal.downloadSuccess'))
          return
        } catch (mediaError) {
          console.error('Media plugin error:', mediaError)
          // Fallback: eski yöntem
          const response = await fetch(imageUrl)
          const blob = await response.blob()
          const arrayBuffer = await blob.arrayBuffer()
          const bytes = new Uint8Array(arrayBuffer)
          let binary = ''
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
          const base64Data = typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64')

          await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Documents,
          })
          alert(t('imageModal.downloadFallback'))
          return
        }
      }

      // Web fallback: anchor download
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading image:', error)
      alert(t('imageModal.downloadError'))
    }
  }

  const handleShare = async () => {
    try {
      const fileName = `share-${playerName || 'unknown'}-round-${roundNumber || 1}.jpg`

      if (Capacitor.isNativePlatform()) {
        // Native: resmi dosya olarak kaydet ve dosya URI'si ile paylaş
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const arrayBuffer = await blob.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)
        let binary = ''
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
        const base64Data = typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64')

        const writeResult = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
        })

        await Share.share({
          title: t('imageModal.shareTitle', { round: roundNumber }),
          text: promptText || t('imageModal.shareText'),
          url: writeResult.uri,
          dialogTitle: 'Görseli paylaş',
        })
        return
      }

      // Web: mevcut share API ile URL paylaş; Level 2 destekliyse dosya paylaşımına yükseltilebilir
      if (navigator.share) {
        await navigator.share({
          title: t('imageModal.shareTitle', { round: roundNumber }),
          text: promptText || t('imageModal.shareText'),
          url: imageUrl
        })
        return
      }

      // Web fallback: URL'i panoya kopyala
      await navigator.clipboard.writeText(imageUrl)
      alert(t('imageModal.shareUrlCopied'))
    } catch (error) {
      console.error('Error sharing:', error)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed top-0 left-0 w-screen h-screen z-50 bg-black/100 backdrop-blur-sm"
          style={{ 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh',
            margin: 0,
            padding: 0
          }}
          onClick={onClose}
        >
          {/* Close button */}
          <div className="absolute top-4 right-4 z-10 safe-area">
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={onClose}
              className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </motion.button>
          </div>

          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute top-4 left-4 z-10 flex space-x-2 safe-area"
          >
            <button
              onClick={handleDownload}
              className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              title={t('imageModal.downloadTitle')}
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={handleShare}
              className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              title={t('imageModal.shareButtonTitle')}
            >
              <Share2 className="w-5 h-5" />
            </button>
          </motion.div>

          {/* Image container */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="absolute top-0 left-0 w-screen h-screen flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              </div>
            )}
            
            <img
              src={imageUrl}
              alt={imageAlt}
              className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
            />
          </motion.div>

          {/* Image info */}
          {(promptText || playerName) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-4 left-4 right-4 z-10 safe-area"
            >
              <div className="bg-black/60 backdrop-blur-md rounded-xl p-4 border border-white/20">
                {playerName && (
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {playerName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-white font-medium">{playerName}</span>
                    {roundNumber && (
                      <span className="text-white/60 text-sm">• Round {roundNumber}</span>
                    )}
                  </div>
                )}
                {promptText && (
                  <p className="text-white/90 text-sm leading-relaxed">{promptText}</p>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
